import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { checkReviewNote } from '@psy/validations';

import { useNoteChanges } from '../../hooks/note-changes';
import { unreachable } from '../../hooks/request';
import { useSession } from '../session';

const NoteSavesContext = createContext();

// How long typing has to pause before a note saves on its own.
const pause = 800;

// The clinician's private notes on submitted tests, and whether each is saved
// yet (#132). A note saves as it is written, and a clinician can leave the
// review, reopen it or open another attempt before a save has settled, so the
// saves live above every screen rather than in the field on screen: one
// writer per note, whichever field shows it.
//
// Each note (an attempt's answer, or the whole test) has one entry: the latest
// text, the text the service last acknowledged, whether that is certain, the
// typing pause, whether a write is on its way and the last write's failure.
// Writes go one at a time and always end with the latest text: text typed
// while a write is out waits, text in between that was never sent is dropped,
// and a write once sent is never taken back, since it may already have been
// applied. So an older text can never land after a newer one, whichever field
// it was typed in and whatever order the network answers in.
//
// What the service holds is known only from its answers (#127). A save it
// refused was not applied, so the stored text stands. A save whose answer
// never arrived may well have been applied: the stored text is then
// uncertain, and the latest text is sent again even when it equals the text
// stored before. The empty note is a valid text like any other.
//
// Entries belong to one session lifetime: the clinician signed in now, from
// sign-in to sign-out. The provider lives inside components/account-lifetime
// (#138), which mounts it afresh whenever the session ends or changes hands,
// even back to the same clinician, so a lifetime is made once, when the
// provider mounts, and retired when it unmounts: nothing of it is sent any
// more, a write already out is left to settle on its own, and its answer
// changes nothing on screen. Every save also names its clinician, which the
// service checks, for the session that changes hands in another tab.
//
// Signing out is one transition of the lifetime, shared by every screen
// (#134). From the moment it starts no note takes an edit, so the notes it
// saves are every note accepted before it, wherever they were typed; fields
// opened meanwhile are read-only. When it stops short, because a note could
// not be saved or the session could not be ended, editing comes back with
// the reason, on Settings and on any review, until the next edit or attempt.
// ended records a session the service did end although the page could not
// be updated: nothing can be saved under it any more.
export function NoteSavesProvider({ children }) {
  const { me } = useSession();
  const saveNote = useNoteChanges();
  const [ version, setVersion ] = useState(0);

  const [ lifetime ] = useState(function() {
    let owner = null;

    if (me != null) {
      owner = me.id;
    }

    return { owner, retired: false, holding: false, signingOut: false, signOutFailure: undefined, ended: false, entries: new Map() };
  });

  useEffect(function() {
    return function() {
      lifetime.retired = true;
      lifetime.entries.forEach(function(entry) {
        clearTimeout(entry.timer);
      });
    };
  }, [ lifetime ]);

  const changed = useCallback(function() {
    setVersion(function(current) {
      return current + 1;
    });
  }, []);

  // Sends the latest text when it is not known to be stored, nothing is out
  // yet and typing has paused. Each settled write sends again if a newer text
  // arrived meanwhile, and whoever waits on the note hears once it settles.
  const pump = useCallback(function(entry) {
    const { owner: clinicianId, retired, holding } = entry.lifetime;

    if (retired === true || holding === true || entry.sending === true || entry.timer != null || entry.failure != null) {
      return;
    }

    if (checkReviewNote(entry.latest) != null || (entry.certain === true && entry.latest === entry.stored)) {
      return;
    }

    const note = entry.latest;

    entry.sending = true;
    changed();

    saveNote({ clinicianId, attemptId: entry.attemptId, position: entry.position, note }).then(function() {
      entry.stored = note;
      entry.certain = true;
    }).catch(function(failed) {
      if (failed.message === unreachable) {
        entry.certain = false;
      }

      // A failure belongs to the text it tried to save: when a newer one has
      // been typed since, that one is simply sent next.
      if (entry.latest === note) {
        entry.failure = failed.message;
      }
    }).then(function() {
      entry.sending = false;

      if (entry.lifetime.retired === false) {
        changed();
        pump(entry);
      }

      if (entry.sending === false) {
        const waiting = entry.waiting;

        entry.waiting = [];
        waiting.forEach(function(resolve) {
          resolve();
        });
      }
    });
  }, [ saveNote, changed ]);

  // The entry for one note, created from what the service holds the first
  // time it opens in this lifetime. A later opening keeps the entry, so a
  // write still on its way, text not yet sent and a failure not yet resolved
  // are not forgotten. position is null for the note on the whole test.
  const entryOf = useCallback(function({ attemptId, position, initial }) {
    const key = `${ attemptId }:${ position }`;

    if (lifetime.entries.has(key) === false) {
      let stored = '';

      if (initial != null) {
        stored = initial;
      }

      lifetime.entries.set(key, {
        lifetime, attemptId, position, latest: stored, stored, certain: true,
        touched: false, timer: undefined, sending: false, failure: undefined, waiting: []
      });
    }

    return lifetime.entries.get(key);
  }, [ lifetime ]);

  const flush = useCallback(function(entry) {
    clearTimeout(entry.timer);
    entry.timer = undefined;
    entry.failure = undefined;
    changed();
    pump(entry);
  }, [ changed, pump ]);

  // An edit made while signing out, or after the session ended, is not
  // accepted: the field is read-only then, and this holds for any caller.
  const edit = useCallback(function(entry, text) {
    if (entry.lifetime.signingOut === true || entry.lifetime.ended === true) {
      return;
    }

    entry.lifetime.signOutFailure = undefined;
    entry.latest = text;
    entry.touched = true;
    entry.failure = undefined;
    clearTimeout(entry.timer);
    entry.timer = setTimeout(function() {
      entry.timer = undefined;
      changed();
      pump(entry);
    }, pause);
    changed();
  }, [ changed, pump ]);

  // Sends every note not yet saved, trying failed ones again, and settles
  // once nothing is out with whether every note is now saved: what signing
  // out waits for. The answer covers every note of the lifetime, opened
  // before or during the wait alike.
  const settleAll = useCallback(function() {
    const entries = Array.from(lifetime.entries.values());

    entries.forEach(function(entry) {
      clearTimeout(entry.timer);
      entry.timer = undefined;
      entry.failure = undefined;
      pump(entry);
    });
    changed();

    return Promise.all(entries.map(function(entry) {
      if (entry.sending === false) {
        return null;
      }

      return new Promise(function(resolve) {
        entry.waiting.push(resolve);
      });
    })).then(function() {
      return Array.from(lifetime.entries.values()).every(function(entry) {
        return entry.failure == null && entry.certain === true && entry.latest === entry.stored;
      });
    });
  }, [ lifetime, changed, pump ]);

  // Holds every new write while the session is being ended, and lets them go
  // again when it could not be.
  const hold = useCallback(function() {
    lifetime.holding = true;
  }, [ lifetime ]);

  const release = useCallback(function() {
    lifetime.holding = false;
    lifetime.entries.forEach(function(entry) {
      pump(entry);
    });
  }, [ lifetime, pump ]);

  const startSignOut = useCallback(function() {
    lifetime.signingOut = true;
    lifetime.signOutFailure = undefined;
    changed();
  }, [ lifetime, changed ]);

  // failure is the sentence the sign-out stopped with; ended says the
  // service did end the session.
  const stopSignOut = useCallback(function({ failure, ended }) {
    lifetime.signingOut = false;
    lifetime.signOutFailure = failure;

    if (ended === true) {
      lifetime.ended = true;
    }

    changed();
  }, [ lifetime, changed ]);

  const { signingOut, signOutFailure, ended } = lifetime;

  const value = useMemo(function() {
    return { version, signingOut, signOutFailure, ended, entryOf, edit, flush, settleAll, hold, release, startSignOut, stopSignOut };
  }, [ version, signingOut, signOutFailure, ended, entryOf, edit, flush, settleAll, hold, release, startSignOut, stopSignOut ]);

  return <NoteSavesContext.Provider value={ value }>{ children }</NoteSavesContext.Provider>;
}

export function useNoteSaves() {
  return useContext(NoteSavesContext);
}
